/**
 * React error boundary that reports render-time crashes to RUM (with the
 * breadcrumb trail attached) and shows a fallback instead of a white screen.
 * React render errors are NOT seen by aws-rum-web's window.onerror telemetry,
 * so this boundary is how they reach RUM.
 *
 * It passes ONLY safe, low-cardinality fields to RUM — never the component
 * stack, props, or state (those can contain clinical data). `componentName`
 * lets you locate the boundary that tripped.
 */
import { Component, type ReactNode } from 'react';

import { recordError } from './rumClient';

interface RumErrorBoundaryProps {
  children: ReactNode;
  /** Low-cardinality label for the area this boundary guards (no PII). */
  componentName: string;
  /** Optional custom fallback UI. */
  fallback?: ReactNode;
}

interface RumErrorBoundaryState {
  hasError: boolean;
}

export class RumErrorBoundary extends Component<
  RumErrorBoundaryProps,
  RumErrorBoundaryState
> {
  constructor(props: RumErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RumErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    // Deliberately ignore React's errorInfo.componentStack — it can echo
    // rendered clinical content. Only the error + safe labels go to RUM.
    recordError(error, {
      componentName: this.props.componentName,
      recoverable: false,
      fallbackShown: true,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
